import { DPR, POPUP_DEPTH } from '@constants/common.const';
import { SceneKey } from '@models/scene-keys';
import { GameConfigService } from '@services/core/GameConfigService';
import { LayoutService } from '@services/ui/LayoutService';
import Phaser from 'phaser';
import {
  preloadCelebration,
  createCelebrationSprite,
  playAnimation,
} from '../ui/animations/CelebrationFx';
import { Button } from '../ui/Button';

export type RewardSceneConfig = {
  title: string;
  subtitle: string;
  iconKey: string;
  rewardLabel: string;
  ctaLabel: string;
  onCTAClick: () => void;
  onCloseClick?: () => void;
};

export default class Reward extends Phaser.Scene {
  constructor() {
    super(SceneKey.Reward);
  }

  private layout!: LayoutService;
  private cfg!: GameConfigService;
  private bg!: Phaser.GameObjects.Rectangle;
  private closeBtn!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private titleText!: Phaser.GameObjects.Text;
  private icon!: Phaser.GameObjects.Image;
  private rewardLabel!: Phaser.GameObjects.Text;
  private ctaButton!: Button;
  private celebrationSprite?: Phaser.GameObjects.Sprite;
  private sceneConfig!: RewardSceneConfig;

  preload() {
    preloadCelebration(this);
  }

  create(data: RewardSceneConfig) {
    this.sceneConfig = data;
    this.layout = new LayoutService(this.scale);
    this.cfg = new GameConfigService();

    this.buildUI();
    this.animateEntrance();

    this.scene.bringToTop();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.scale.on('resize', this.handleResize, this);
  }

  private buildUI() {
    const { width, height } = this.scale;
    const cx = this.layout.centerX();
    const accentColor = this.cfg.getColor('--color-accent-1')!;
    const textColor =
      this.cfg.getColorHex('--z-result-page-text-color') ??
      this.cfg.getColorHex('--color-neutral-100') ??
      '#FFFFFF';
    this.bg = this.add
      .rectangle(0, 0, width, height, accentColor, 1)
      .setOrigin(0, 0)
      .setDepth(POPUP_DEPTH)
      .setScrollFactor(0)
      .setInteractive();

    this.closeBtn = this.add
      .text(width - 24 * DPR, 24 * DPR, '✕', {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${24 * DPR}px`,
        color: textColor,
        resolution: DPR,
      })
      .setAlpha(0.5)
      .setOrigin(1, 0)
      .setDepth(POPUP_DEPTH + 2)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    this.closeBtn.on('pointerup', () => {
      if (this.sceneConfig.onCloseClick) {
        this.sceneConfig.onCloseClick();
      } else {
        this.scene.stop(SceneKey.Reward);
      }
    });

    this.subtitleText = this.add
      .text(cx, 0, this.sceneConfig.subtitle.toUpperCase(), {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${17 * DPR}px`,
        fontStyle: '700',
        color: textColor,
        resolution: DPR,
      })
      .setAlpha(0.7)
      .setOrigin(0.5)
      .setDepth(POPUP_DEPTH + 2)
      .setScrollFactor(0);

    this.titleText = this.add
      .text(cx, 0, this.sceneConfig.title, {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${24 * DPR}px`,
        fontStyle: '700',
        color: textColor,
        resolution: DPR,
      })
      .setOrigin(0.5, 0)
      .setDepth(POPUP_DEPTH + 2)
      .setScrollFactor(0);

    const iconSize = 128 * DPR;
    this.icon = this.add
      .image(cx, 0, this.sceneConfig.iconKey)
      .setDisplaySize(iconSize, iconSize)
      .setOrigin(0.5)
      .setDepth(POPUP_DEPTH + 2)
      .setScrollFactor(0);

    this.rewardLabel = this.add
      .text(cx, 0, this.sceneConfig.rewardLabel, {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${32 * DPR}px`,
        fontStyle: '700',
        color: textColor,
        align: 'center',
        wordWrap: {
          width: this.layout.screenWidth * 0.9,
          useAdvancedWrap: true,
        },
        resolution: DPR,
      })
      .setOrigin(0.5, 0)
      .setDepth(POPUP_DEPTH + 2)
      .setScrollFactor(0);

    this.ctaButton = new Button({
      scene: this,
      x: cx,
      y: 0,
      text: this.sceneConfig.ctaLabel,
      type: 'secondary',
      onClick: () => {
        this.sceneConfig.onCTAClick();
      },
    });
    this.ctaButton.setDepth(POPUP_DEPTH + 2).setScrollFactor(0);

    this.layoutElements();

    this.celebrationSprite =
      createCelebrationSprite(this, {
        x: cx,
        y: this.icon.y - this.icon.displayHeight / 2,
        depth: POPUP_DEPTH + 1,
        scrollFactor: 0,
      }) ?? undefined;

    if (this.celebrationSprite) {
      playAnimation(this, this.celebrationSprite, {
        repeat: 1,
        timeScale: 1.2,
      });
      this.celebrationSprite.setScale(1.3);
      this.celebrationSprite.once('animationcomplete', () => {
        this.celebrationSprite?.destroy();
      });
    }
  }

  private layoutElements() {
    const { height } = this.scale;
    const cx = this.layout.centerX();
    const BUTTON_HALF_H = 20 * DPR; // Button is 40*DPR tall, center-anchored

    // subtitle — origin (0.5, 0.5)
    this.subtitleText.setPosition(cx, height * 0.3);

    // title top = subtitle bottom + 16px — origin (0.5, 0)
    this.titleText.setPosition(
      cx,
      this.subtitleText.y + this.subtitleText.height / 2 + 16 * DPR,
    );

    // icon center = title bottom + 32px — origin (0.5, 0.5)
    const iconHalfH = this.icon.displayHeight / 2;
    this.icon.setPosition(
      cx,
      this.titleText.y + this.titleText.height + 32 * DPR + iconHalfH,
    );
    this.celebrationSprite?.setPosition(cx, this.icon.y);

    // reward top = icon bottom + 32px — origin (0.5, 0)
    this.rewardLabel.setPosition(cx, this.icon.y + iconHalfH + 32 * DPR);

    // button center = reward bottom + 32px + half button height
    this.ctaButton.setPosition(
      cx,
      this.rewardLabel.y + this.rewardLabel.height + 32 * DPR + BUTTON_HALF_H,
    );
  }

  private animateEntrance() {
    const dropDistance = 30 * DPR;
    const items = [this.subtitleText, this.titleText, this.icon];

    items.forEach((obj, i) => {
      const targetY = obj.y;
      obj.setAlpha(0).setY(targetY - dropDistance);

      this.tweens.add({
        targets: obj,
        alpha: 1,
        y: targetY,
        duration: 700,
        delay: 100 + i * 180,
        ease: 'Cubic.Out',
      });
    });

    [this.rewardLabel, this.ctaButton].forEach((obj, i) => {
      obj.setAlpha(0);
      this.tweens.add({
        targets: obj,
        alpha: 1,
        duration: 600,
        delay: 600 + i * 180,
        ease: 'Cubic.Out',
      });
    });
  }

  private handleResize() {
    if (!this.scene?.isActive()) return;

    const { width } = this.scale;
    this.bg.setSize(width, this.scale.height);
    this.closeBtn.setPosition(width - 24 * DPR, 24 * DPR);
    this.layoutElements();
  }

  shutdown() {
    this.scale.off('resize', this.handleResize, this);
  }
}
